export interface CameraFrame {
  zoom: number;
  centerY: number;
}

export interface CameraTransition {
  startZoom: number;
  targetZoom: number;
  elapsedMs: number;
  durationMs: number;
}

export interface WorldRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export const AIMING_STACK_SAFETY_GAP = 80;
export const CAMERA_NUMERICAL_ZOOM_FLOOR = 0.001;
export const CAMERA_TRANSITION_MS = 1100;
const MOBILE_ANIMAL_LEAD_FACTOR = 1.5;
const MOBILE_VISIBLE_ANIMAL_HEIGHTS = 2.5;
const MOBILE_VIEWPORT_RESERVE_RATIO = 0.12;
const MOBILE_TRANSITION_MIN_MS = 220;
const MOBILE_TRANSITION_MAX_MS = 800;
const MOBILE_TRANSITION_SCREEN_RATIO = 0.25;
export const START_SUPPORT_SCREEN_MARGIN = 12;

export function minimumCameraZoomForViewport(_viewport: ViewportMetrics = DESKTOP_VIEWPORT_METRICS): number {
  return CAMERA_NUMERICAL_ZOOM_FLOOR;
}

function clampZoom(value: number, viewport: ViewportMetrics = DESKTOP_VIEWPORT_METRICS): number {
  const minimum = minimumCameraZoomForViewport(viewport);
  return Math.min(1, Math.max(minimum, Number.isFinite(value) ? value : 1));
}

export function cameraZoomToFitWorldWidth(
  worldWidth: number,
  viewport: ViewportMetrics = DESKTOP_VIEWPORT_METRICS,
  screenMargin = START_SUPPORT_SCREEN_MARGIN,
): number {
  if (!Number.isFinite(worldWidth) || worldWidth <= 0) return 1;
  const safeMargin = Number.isFinite(screenMargin) ? Math.max(0, screenMargin) : 0;
  const availableScreenWidth = Math.max(0, viewport.stageWidth - safeMargin * 2);
  return clampZoom(availableScreenWidth / worldWidth, viewport);
}

export function cameraTargetZoomForPlacedBottom(
  placedBottomY: number,
  supportAnchorWorldY: number,
  aimingTopExtentWorld: number,
  viewport: ViewportMetrics = DESKTOP_VIEWPORT_METRICS,
  aimingHeightWorld?: number,
): number {
  const worldSpan = placedBottomY - supportAnchorWorldY;
  if (!Number.isFinite(worldSpan) || worldSpan < 0
    || !Number.isFinite(aimingTopExtentWorld) || aimingTopExtentWorld < 0) return 1;
  const availableScreenSpan = viewport.spawnScreenY - viewport.supportScreenY;
  if (viewport.layoutMode === 'desktop' || aimingHeightWorld === undefined) {
    return clampZoom(
      (availableScreenSpan - AIMING_STACK_SAFETY_GAP) / (worldSpan + aimingTopExtentWorld),
      viewport,
    );
  }
  if (!Number.isFinite(aimingHeightWorld) || aimingHeightWorld <= 0) return 1;
  const stackAndAnimalSpan = worldSpan + aimingTopExtentWorld;
  const requiredWorldSpan = Math.max(
    stackAndAnimalSpan + aimingHeightWorld * MOBILE_ANIMAL_LEAD_FACTOR,
    aimingHeightWorld * MOBILE_VISIBLE_ANIMAL_HEIGHTS,
  );
  const zoomByAnimal = availableScreenSpan / requiredWorldSpan;
  const viewportReserve = viewport.stageHeight * MOBILE_VIEWPORT_RESERVE_RATIO;
  const zoomByViewport = stackAndAnimalSpan > 0
    ? (availableScreenSpan - viewportReserve) / stackAndAnimalSpan
    : 1;
  return clampZoom(Math.min(zoomByAnimal, zoomByViewport), viewport);
}

export function cameraFrameForZoom(
  zoom: number,
  supportAnchorWorldY: number,
  viewport: ViewportMetrics = DESKTOP_VIEWPORT_METRICS,
): CameraFrame {
  const safeZoom = clampZoom(zoom, viewport);
  return {
    zoom: safeZoom,
    centerY: supportAnchorWorldY + (viewport.canvasCenterY - viewport.supportScreenY) / safeZoom,
  };
}

export function cameraWorldViewBoundsAtMinimumZoom(
  centerWorldX: number,
  supportAnchorWorldY: number,
  viewport: ViewportMetrics = DESKTOP_VIEWPORT_METRICS,
  margin = 0,
): WorldRect {
  const safeMargin = Number.isFinite(margin) ? Math.max(0, margin) : 0;
  const frame = cameraFrameForZoom(
    minimumCameraZoomForViewport(viewport),
    supportAnchorWorldY,
    viewport,
  );
  const halfWidth = viewport.stageWidth / frame.zoom / 2;
  const halfHeight = viewport.stageHeight / frame.zoom / 2;
  return {
    left: centerWorldX - halfWidth - safeMargin,
    right: centerWorldX + halfWidth + safeMargin,
    top: frame.centerY - halfHeight - safeMargin,
    bottom: frame.centerY + halfHeight + safeMargin,
  };
}

export function beginCameraTransition(
  currentZoom: number,
  targetZoom: number,
  viewport: ViewportMetrics = DESKTOP_VIEWPORT_METRICS,
): CameraTransition {
  const startZoom = clampZoom(currentZoom, viewport);
  const safeTargetZoom = clampZoom(targetZoom, viewport);
  if (viewport.layoutMode === 'desktop') {
    return { startZoom, targetZoom: safeTargetZoom, elapsedMs: 0, durationMs: CAMERA_TRANSITION_MS };
  }
  const projectedScreenTravel = Math.abs(startZoom - safeTargetZoom) * viewport.usablePlayHeight;
  const referenceTravel = Math.max(1, viewport.stageHeight * MOBILE_TRANSITION_SCREEN_RATIO);
  const travelRatio = Math.min(1, projectedScreenTravel / referenceTravel);
  const durationMs = MOBILE_TRANSITION_MIN_MS
    + (MOBILE_TRANSITION_MAX_MS - MOBILE_TRANSITION_MIN_MS) * travelRatio;
  return { startZoom, targetZoom: safeTargetZoom, elapsedMs: 0, durationMs };
}

export function advanceCameraTransition(
  transition: CameraTransition,
  deltaMs: number,
  supportAnchorWorldY: number,
  viewport: ViewportMetrics = DESKTOP_VIEWPORT_METRICS,
): { transition: CameraTransition; frame: CameraFrame } {
  const safeDelta = Number.isFinite(deltaMs) ? Math.max(0, deltaMs) : 0;
  const durationMs = Number.isFinite(transition.durationMs) && transition.durationMs > 0
    ? transition.durationMs
    : CAMERA_TRANSITION_MS;
  const elapsedMs = Math.min(durationMs, transition.elapsedMs + safeDelta);
  const progress = elapsedMs / durationMs;
  const eased = Math.sin(progress * Math.PI / 2);
  const zoom = transition.startZoom + (transition.targetZoom - transition.startZoom) * eased;
  const next = { ...transition, elapsedMs };
  return { transition: next, frame: cameraFrameForZoom(zoom, supportAnchorWorldY, viewport) };
}
import { DESKTOP_VIEWPORT_METRICS, type ViewportMetrics } from './viewportMetrics';
